import { ContentType } from '@smart-tv/typings/content';

import { kmmRequest } from './kmm';

export interface GetNextEventForContentIdParams {
  contentId: number;
  contentType?: ContentType | string | null;
  context?: Record<string, unknown>;
}

interface NextEventForSeriesRaw {
  [key: string]: unknown;
}

interface NextEventProviderVersion {
  airTimeStart?: number;
  resolution?: string;
  eventId: number;
  sid: number;
  price?: number;
  isRented?: boolean;
  language?: string;
  subtitles?: string;
  secondsWatched: number;
  unPublishDate?: number;
  npvrInfo?: {
    notSeen: boolean;
    expireAt: number;
    recordedAt: number;
    npvrTrickmodes: number;
    customerRecording: boolean;
    isSeriesRecording: boolean;
    recordingStatus: string;
    enoughSpaceForRecording: boolean;
    isNpvrExtendAllowed: boolean;
    extendNpvrEventWithInSec: number;
    npvrMediaId: number;
    expireSoon: boolean;
  };
  rentalExpire?: number;
  latestWatched?: boolean;
  shouldShowContinuePrompt?: boolean;
  mediaId?: number;
}

interface NextEventProvider {
  providerType?: string;
  providerName: string;
  length: number;
  covers: {
    current?: string;
  };
  hasTrailer?: boolean;
  rentalPeriod?: number;
  unPublishDate?: number;
  access?: boolean;
  previewDuration?: number;
  trailerMediaId?: number;
  secondsWatched: number;
  missingSubscriptionText?: string;
  latestWatched?: boolean;
  providerKey?: string;
  providerId?: number;
  versions: NextEventProviderVersion[];
}

export interface NextEventForSeriesEpisode {
  providers: NextEventProvider[];
  episodeTitle: string;
  shortDescription: string;
  longDescription: string;
  episodeNumber: number;
  seriesId: number;
  seasonId: number;
  seasonNumber: number;
  cast: Array<{
    personId: number;
    firstName: string;
    lastName: string;
    roleName: string;
    job: string;
  }>;
  episodeId: number;
  contentType: string;
  cover: string;
  channelIcon?: string;
  readableAirTimeOrRecordingTime?: string;
  isNextEventforSeries: boolean;
}

interface NextEventForSeriesRawResponse {
  data?: NextEventForSeriesRaw;
}

const toObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return toObjectRecord(parsed);
    } catch {
      return null;
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
};

const extractNextEventData = (jsonResponse: string): NextEventForSeriesRaw | null => {
  const parsedResponse = JSON.parse(jsonResponse) as NextEventForSeriesRawResponse &
    Record<string, unknown>;

  const candidates = [
    parsedResponse.data,
    parsedResponse,
    (parsedResponse.result as Record<string, unknown> | undefined)?.data
  ];

  for (const candidate of candidates) {
    const record = toObjectRecord(candidate);
    if (record) {
      return record;
    }
  }

  return null;
};

const fetchUpcomingEpisode = async (
  contentId: number,
  requestParams?: Record<string, unknown>
): Promise<NextEventForSeriesRaw | null> => {
  const requestNextEvent = async (
    params: Record<string, unknown>
  ): Promise<NextEventForSeriesRaw | null> => {
    try {
      // Optional UX enhancement: never break sheet rendering if this endpoint fails.
      const jsonResponse = await kmmRequest('getNextEventForSeriesId', params, false);
      return extractNextEventData(jsonResponse);
    } catch {
      return null;
    }
  };

  const paramsWithContext: Record<string, unknown> = {
    contentId,
    seriesId: contentId,
    ...(requestParams ?? {})
  };

  const hasContext = Boolean(requestParams && Object.keys(requestParams).length > 0);
  const withContext = await requestNextEvent(paramsWithContext);

  if (withContext || !hasContext) {
    return withContext;
  }

  return requestNextEvent({ contentId, seriesId: contentId, ...(requestParams ?? {}) });
};

const toUnixSeconds = (value: unknown): number => {
  if (typeof value === 'number') {
    if (value > 1000000000000) {
      return Math.floor(value / 1000);
    }
    return Math.floor(value);
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return toUnixSeconds(numeric);
    }

    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return Math.floor(parsed / 1000);
    }
  }

  if (value instanceof Date) {
    return Math.floor(value.getTime() / 1000);
  }

  return 0;
};

const mapNextEventToEpisode = (
  raw: Record<string, unknown>,
  contentId: number
): NextEventForSeriesEpisode => {
  const providersCandidate = Array.isArray(raw.providers) ? raw.providers : [];
  const firstProviderCandidate =
    providersCandidate.length > 0 &&
    providersCandidate[0] &&
    typeof providersCandidate[0] === 'object'
      ? (providersCandidate[0] as Record<string, unknown>)
      : undefined;
  const firstVersionCandidate =
    firstProviderCandidate && Array.isArray(firstProviderCandidate.versions)
      ? firstProviderCandidate.versions[0]
      : undefined;
  const fallbackAirTimeStart =
    (firstVersionCandidate && typeof firstVersionCandidate === 'object'
      ? toUnixSeconds((firstVersionCandidate as Record<string, unknown>).airTimeStart)
      : 0) ||
    toUnixSeconds(raw.airTimeStart) ||
    toUnixSeconds(raw.startTime) ||
    toUnixSeconds((raw.programInfo as Record<string, unknown> | undefined)?.startTime);
  const providerChannelIcon =
    firstProviderCandidate &&
    firstProviderCandidate.covers &&
    typeof firstProviderCandidate.covers === 'object'
      ? String((firstProviderCandidate.covers as Record<string, unknown>).current ?? '')
      : '';

  const provider =
    firstProviderCandidate && Array.isArray(firstProviderCandidate.versions)
      ? firstProviderCandidate
      : {
          providerType: '',
          providerName: String(raw.channelName ?? raw.providerName ?? ''),
          length: Number(raw.length ?? raw.duration ?? 0),
          covers: {
            current: String(raw.channelIcon ?? raw.channelIconUrl ?? '')
          },
          hasTrailer: false,
          rentalPeriod: 0,
          unPublishDate: 0,
          access: true,
          previewDuration: 0,
          trailerMediaId: 0,
          secondsWatched: 0,
          missingSubscriptionText: '',
          latestWatched: false,
          providerKey: '',
          providerId: 0,
          versions: [
            {
              airTimeStart: fallbackAirTimeStart,
              resolution: '',
              eventId: Number(raw.eventId ?? 0),
              sid: Number(raw.sid ?? 0),
              price: 0,
              isRented: false,
              language: '',
              subtitles: '',
              secondsWatched: 0,
              unPublishDate: toUnixSeconds(raw.unPublishDate),
              npvrInfo: {
                notSeen: false,
                expireAt: 0,
                recordedAt: 0,
                npvrTrickmodes: 0,
                customerRecording: false,
                isSeriesRecording: false,
                recordingStatus: '',
                enoughSpaceForRecording: false,
                isNpvrExtendAllowed: false,
                extendNpvrEventWithInSec: 0,
                npvrMediaId: 0,
                expireSoon: false
              },
              rentalExpire: 0,
              latestWatched: false,
              shouldShowContinuePrompt: false,
              mediaId: Number(raw.mediaId ?? 0)
            }
          ]
        };

  const episodeId = Number(raw.episodeId ?? raw.contentId ?? raw.eventId ?? 0);

  return {
    providers: [provider as unknown as NextEventProvider],
    episodeTitle: String(raw.episodeTitle ?? raw.title ?? ''),
    shortDescription: String(raw.shortDescription ?? ''),
    longDescription: String(raw.longDescription ?? raw.description ?? ''),
    episodeNumber: Number(raw.episodeNumber ?? 0),
    seriesId: Number(raw.seriesId ?? contentId),
    seasonId: Number(raw.seasonId ?? 0),
    seasonNumber: Number(raw.seasonNumber ?? 0),
    cast: [],
    episodeId,
    contentType: String(raw.contentType ?? ''),
    cover: String(raw.cover ?? ''),
    channelIcon: String(raw.channelIcon ?? raw.channelIconUrl ?? providerChannelIcon),
    readableAirTimeOrRecordingTime:
      typeof raw.readableAirTimeOrRecordingTime === 'string'
        ? raw.readableAirTimeOrRecordingTime
        : undefined,
    isNextEventforSeries: true
  };
};

const responseMapper = (
  nextEvent: NextEventForSeriesRaw | null,
  contentId: number
): NextEventForSeriesEpisode | null => {
  if (!nextEvent || typeof nextEvent !== 'object') {
    return null;
  }

  return mapNextEventToEpisode(nextEvent, contentId);
};

export async function getNextEventForContentId(
  payload: GetNextEventForContentIdParams
): Promise<NextEventForSeriesEpisode | null> {
  const nextEventRequestParams: Record<string, unknown> = {
    ...(payload.contentType != null ? { contentType: payload.contentType } : {}),
    ...(payload.context ?? {})
  };

  const nextEvent = await fetchUpcomingEpisode(payload.contentId, nextEventRequestParams);
  return responseMapper(nextEvent, payload.contentId);
}

export default getNextEventForContentId;
